import React, { FC, useCallback, useEffect, useState } from 'react'
import { z } from 'zod'
import { Header } from '../../components/header'
import { Steps } from '../../components/steps'

export const Item = z.object({
  id: z.number(),
  title: z.string(),
  image: z.string().nullable()
})

export const Collection = z.array(Item)
export type Collection = z.infer<typeof Collection>

export const Data = z.object({
  header: z.string(),
  label: z.string(),
  metal: Collection,
  metal_info: z.string(),
  wood: Collection,
  wood_info: z.string()
})
export type Data = z.infer<typeof Data>

export type Props = {
  goNextStep: (material: string, design: string) => void
  goBackStep: () => void
}

export const Step_1: FC<Props> = ({ goNextStep, goBackStep }) => {
  const [activeMaterial, setMaterial] = useState<number>(-1)
  const [activeDesign, setDesign] = useState<number>(-1)
  const [activeCollection, setActiveCollection] = useState<Collection>([])

  const [data] = useState<Data | null>(() => {
    const dataContainer = document.getElementById('custom-sizes-step-1')
    const result = Data.safeParse(
      JSON.parse(dataContainer != null ? dataContainer.innerText : '{}')
    )

    if (result.success) {
      return result.data
    }

    console.error(
      'Input data is invalid [adf52123460b4b98b71d34f2fd6a0d16]',
      result.error
    )
    return null
  })

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (data == null) {
      return
    }

    if (activeMaterial === 0) {
      setActiveCollection(data.wood)
    } else if (activeMaterial === 1) {
      setActiveCollection(data.metal)
    } else {
      setActiveCollection([])
    }
    setDesign(-1)
  }, [activeMaterial, data])

  if (data == null) {
    return null
  }

  const options = [
    {
      id: 0,
      title: 'Wood',
      help: data.wood_info
    },
    {
      id: 1,
      title: 'Metal',
      help: data.metal_info
    }
  ]

  function changeMaterial(id: number) {
    setMaterial(id)
  }

  function changeDesign(id: number) {
    setDesign(id)
  }

  function goNext() {
    const material = options[activeMaterial].title
    const design =
      activeCollection[activeDesign] != null
        ? activeCollection[activeDesign].title
        : ''

    goNextStep(material, design)
  }

  const goBack = useCallback(() => {
    goBackStep()
  }, [goBackStep])

  return (
    <div className="CS_Step1">
      <Steps step={1} allSteps={5} />
      {data.header && <Header text={data.header} />}

      <div className="CS_Step1-line">
        {data.label && <label className="CS_Step1-label">{data.label}</label>}

        <div className="CS_Step1-radioContainer">
          {options.length > 0 &&
            options.map((item, i) => (
              <div
                className={`CS_Step1-radio ${
                  item.id === activeMaterial ? 'active' : ''
                } `}
                key={i}
                onClick={() => changeMaterial(item.id)}
                tabIndex={0}
              >
                {item.title}
              </div>
            ))}
        </div>

        <div className="CS_Step1-infoContainer">
          {options.length > 0 &&
            options.map((item, i) => (
              <p key={i}>
                <b>{item.title}:</b> {item.help}
              </p>
            ))}
        </div>
      </div>

      <div className="CS_Step1-thumbnails">
        {activeCollection.length > 0 &&
          activeCollection.map((item, i) => (
            <div
              key={i}
              onClick={() => changeDesign(item.id)}
              className={`CS_Step1-thumbnail ${
                activeDesign === item.id ? 'active' : ''
              }`}
              tabIndex={0}
            >
              <div
                className="CS_Step1-image"
                style={{ backgroundImage: `url(${item?.image})` }}
              ></div>
              <div className="CS_Step1-title">{item?.title}</div>
            </div>
          ))}
      </div>

      <div className="CS_Step1-btnContainer">
        <button className="GoBack" onClick={goBack}>
          <svg
            width="16"
            height="10"
            viewBox="0 0 16 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.75 5L1.25 5M1.25 5L5.54032 0.999999M1.25 5L5.54032 9"
              stroke="#4E6280"
              stroke-width="1.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Back
        </button>
        {activeDesign !== -1 && (
          <button
            className="o-button o-button--blue CS_Step1-btn"
            onClick={goNext}
          >
            Next
          </button>
        )}
      </div>
    </div>
  )
}
